<script setup lang="ts">
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import TableSearchFilter from '@/components/common/TableSearchFilter.vue'
import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import RcSiteSelect from '@/components/rc/common/RcSiteSelect.vue'
import { useSiteDispatches } from '@/composables/eos/site-dispatches'
import { dateTimeFormatter } from '@/helpers/ep/formatters'
import { SiteType } from '@/types/core/Site'
import type { ISiteDispatchFilters } from '@/types/eos/filters'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Icon } from '@iconify/vue'
import { DateTime } from 'luxon'
import { reactive, ref } from 'vue'

const filters = reactive<ISiteDispatchFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[1],
  site_id: undefined,
  date: undefined,
  dispatch_id: undefined,
  program_name: undefined,
})

const { getSiteDispatchesQuery, deleteSiteDispatchCurtailmentPlanMutation } = useSiteDispatches()

const { isFetching, isError, isRefetching, isPending, data, error, refetch } = getSiteDispatchesQuery(filters)

const selectedSite = ref<ISiteIdentifier>()

const onSiteFilterUpdate = (value?: ISiteIdentifier) => {
  filters.site_id = value?.site_id
  filters.page = 1
}

const onDateFilterChange = () => {
  filters.page = 1
}

const programNameSearchSettings = {
  key: 'program_name',
  value: 'Search',
  type: 'search',
  placeholder: 'Program Name',
  onChange: (val: string) => {
    filters.program_name = val === '' ? undefined : val
    filters.page = 1
  },
}

const dispatchIdSearchSettings = {
  key: 'dispatch_id',
  value: 'Search',
  type: 'search',
  placeholder: 'Dispatch ID',
  onChange: (val: string) => {
    filters.dispatch_id = val === '' ? undefined : val
    filters.page = 1
  },
}

const canDeletePlan = (startTimeUtc: string): boolean => {
  const startTime = DateTime.fromISO(startTimeUtc)
  // Show button if time is invalid (safer default) or if it's in the future
  return !startTime.isValid || startTime > DateTime.now()
}

const handleDeletePlan = (siteDispatchID: number, curtailmentPlanID: number, planName: string) => {
  DeleteConfirmationModal(
    'Delete Curtailment Plan',
    `Are you sure you want to delete the curtailment plan "${planName}"? This action cannot be undone.`,
    curtailmentPlanID,
    () => deleteSiteDispatchCurtailmentPlanMutation.mutateAsync({ siteDispatchID, curtailmentPlanID })
  )
}
</script>

<template>
  <ViewLayout title="Site Dispatches">
    <rl-error-result v-if="isError && !isRefetching" :error="error">
      <el-button @click="refetch()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else>
      <div class="flex gap-2">
        <RcSiteSelect
          v-model="selectedSite"
          placeholder="Site ID or Name"
          :type="SiteType.DataCenter"
          :disabled="isPending"
          style="width: 200px"
          clearable
          @update:model-value="onSiteFilterUpdate"
        />
        <el-date-picker
          v-model="filters.date"
          type="date"
          placeholder="Pick a date"
          value-format="YYYY-MM-DD"
          style="width: 200px"
          :disabled="isPending"
          clearable
          @change="onDateFilterChange"
        />
        <TableSearchFilter :search-settings="programNameSearchSettings" :disabled="isPending" />
        <TableSearchFilter :search-settings="dispatchIdSearchSettings" :disabled="isPending" />
      </div>
      <el-table v-loading="isFetching" :data="data?.data" empty-text="No data" stripe>
        <el-table-column type="expand">
          <template #default="scope">
            <el-table :data="scope.row.curtailment_plans" empty-text="No curtailment plans" stripe size="small">
              <el-table-column label="Curtailment Plans" header-align="center">
                <el-table-column prop="id" label="ID" :min-width="64" />
                <el-table-column prop="name" label="Name" :min-width="180" />
                <el-table-column prop="foreman_plan_id" label="Foreman Plan ID" :min-width="64" />
                <el-table-column prop="foreman_client_id" label="Foreman Client ID" :min-width="64" />
                <el-table-column
                  prop="start_time_utc"
                  label="Execution time"
                  :min-width="120"
                  :formatter="dateTimeFormatter"
                />
                <el-table-column prop="direction" label="Direction" :min-width="60" align="center">
                  <template #default="scope">
                    <span v-if="scope.row.direction?.toLowerCase() === 'down'" class="text-red-500 font-bold">
                      DOWN
                    </span>
                    <span v-else-if="scope.row.direction?.toLowerCase() === 'up'" class="text-green-500 font-bold">
                      UP
                    </span>
                    <span v-else>UNKNOWN</span>
                  </template>
                </el-table-column>
                <el-table-column label="Actions" :min-width="50" align="center">
                  <template #default="planScope">
                    <el-button v-if="canDeletePlan(planScope.row.start_time_utc)" link>
                      <Icon
                        icon="material-symbols:delete-outline"
                        :height="20"
                        @click="handleDeletePlan(scope.row.id, planScope.row.id, planScope.row.name)"
                      />
                    </el-button>
                  </template>
                </el-table-column>
              </el-table-column>
            </el-table>
          </template>
        </el-table-column>
        <el-table-column prop="id" label="ID" :min-width="64" />
        <el-table-column label="Site" :min-width="140">
          <template #default="scope">
            {{ scope.row.site?.name || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="dispatch_id" label="Dispatch ID" :min-width="110" />
        <el-table-column prop="start_time_utc" label="Start Time" :min-width="200" :formatter="dateTimeFormatter" />
        <el-table-column prop="end_time_utc" label="End Time" :min-width="200" :formatter="dateTimeFormatter" />
        <el-table-column label="Program" :min-width="250">
          <template #default="scope">
            {{ scope.row.program_name }}
          </template>
        </el-table-column>
        <el-table-column prop="cancelled" label="Cancelled" :min-width="100" align="center">
          <template #default="scope">
            <el-tag :type="scope.row.cancelled ? 'danger' : 'success'" size="small">
              {{ scope.row.cancelled ? 'Yes' : 'No' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Overlapped by Dispatch" :min-width="110">
          <template #default="scope">
            {{ scope.row.overlapped_by_dispatch || '-' }}
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="data && data.total_pages > 0"
        v-model:current-page="filters.page"
        v-model:page-size="filters.per_page"
        class="flex justify-end mt-4"
        background
        :layout="DEFAULT_PAGINATION_LAYOUT"
        :page-sizes="DEFAULT_PAGE_SIZES"
        :page-count="data.total_pages"
        :total="data.total_records"
        :disabled="isPending"
        hide-on-single-page
      />
    </template>
  </ViewLayout>
</template>
